export const SyncOperation = { Create: 0, Update: 1, Delete: 2 } as const
export type SyncOperationType = typeof SyncOperation[keyof typeof SyncOperation]

export const SyncStatus = { Applied: 0, Conflict: 1, ShiftLocked: 2, NotFound: 3, Error: 4, NotPermitted: 5, Retry: 6 } as const
export type SyncStatusType = typeof SyncStatus[keyof typeof SyncStatus]

export interface StoppageMutation {
  clientId: string
  operation: SyncOperationType
  stoppageId?: number
  rowVersion?: string
  shiftId: number
  data?: unknown
}

export interface LoadingMutation {
  clientId: string
  operation: SyncOperationType
  loadingId?: number
  rowVersion?: string
  shiftId: number
  data?: unknown
}

export interface DiggingPatternMutation {
  clientId: string
  operation: SyncOperationType
  diggingPatternId?: number
  rowVersion?: string
  shiftId: number
  data?: unknown
}

export interface CompleteShiftMutation {
  clientId: string
  shiftId: number
}

export interface GeoRehandleMutation {
  clientId: string
  operation: SyncOperationType
  geoRehandleId?: number
  rowVersion?: string
  shiftId?: number
  data?: unknown
}

export interface OperationLocationMutation {
  clientId: string
  operationId: number
  locationId: number
  shiftId?: number
  operationPlantId?: number
}

export interface EngineHoursMutation {
  clientId: string
  operationId: number
  shiftId: number
  startEngineHours?: number
  endEngineHours?: number
  fuel?: number
  operationPlantId?: number
}

export interface DrillingSessionMutation {
  clientId: string
  shiftId: number
  rigId: number
  drillingTypeId: number
  drillingId?: number
}

export interface DrillingPatternMutation {
  clientId: string
  operation: SyncOperationType
  shiftId: number
  drillingBlastId?: number
  drillingId: number
  patternBlastId: number
  entryMode: number
}

export interface DrillingLineMutation {
  clientId: string
  operation: SyncOperationType
  shiftId: number
  lineId?: number
  rowVersion?: string
  data?: unknown
}

export interface OperationMeterMutation {
  clientId: string
  operationId: number
  shiftId: number
  operationPlantId?: number
  meterType: number
  startReading?: number | null
  endReading?: number | null
  meterReplaced: boolean
  notes?: string | null
  rowVersion?: string | null
}

export interface GcLineMutation {
  clientId: string
  operation: SyncOperationType
  shiftId: number
  lineId?: number
  rowVersion?: string
  data?: unknown
}

export interface DrilledHoleMutation {
  clientId: string
  operation: SyncOperationType
  shiftId: number
  attemptId?: number
  rowVersion?: string
  data?: unknown
}

export interface HoleDipMutation {
  clientId: string
  operation: SyncOperationType
  dipId?: number
  rowVersion?: string
  data?: unknown
}

export interface HoleChargeMutation {
  clientId: string
  blastHoleId: number
  data?: unknown
}

export interface ExplosiveMovementMutation {
  clientId: string
  data?: unknown
}

export interface FiringMutation {
  clientId: string
  operation: SyncOperationType
  patternBlastId: number
  firingId?: number
  rowVersion?: string
  data?: unknown
}

export interface MisfireMutation {
  clientId: string
  patternBlastId: number
  data?: unknown
}

export interface ConsumableMovementMutation {
  clientId: string
  data?: unknown
}

export interface ComponentActionMutation {
  clientId: string
  fit?: unknown
  remove?: unknown
}

export interface SyncRequest {
  diggingPatterns: DiggingPatternMutation[]
  loadings: LoadingMutation[]
  stoppages: StoppageMutation[]
  completeShifts: CompleteShiftMutation[]
  geoRehandles: GeoRehandleMutation[]
  operationLocations: OperationLocationMutation[]
  engineHours: EngineHoursMutation[]
  drillingSessions: DrillingSessionMutation[]
  drillingPatterns: DrillingPatternMutation[]
  drillingLines: DrillingLineMutation[]
  operationMeters: OperationMeterMutation[]
  gcLines: GcLineMutation[]
  drilledHoles: DrilledHoleMutation[]
  holeDips: HoleDipMutation[]
  holeCharges: HoleChargeMutation[]
  explosiveMovements: ExplosiveMovementMutation[]
  firings: FiringMutation[]
  misfires: MisfireMutation[]
  consumableMovements: ConsumableMovementMutation[]
  componentActions: ComponentActionMutation[]
}

export interface SyncMutationResult {
  clientId: string
  status: SyncStatusType
  serverId?: number
  serverState?: unknown
  serverRowVersion?: string
}

export interface SyncResponse {
  results: SyncMutationResult[]
}

export interface ClientSyncFailure {
  occurredAt: string
  clientId: string
  entity: string
  operation: string
  shiftId?: number
  httpStatus?: number
  outcome: string
  message?: string
  context: string
  payload: string
}
